import type { AudioLevelProbe } from "@/modules/audio-recording/legacy";

const PCM_MAX = 32768; // Signed 16-bit divisor so incoming PCM gets normalized to ±1
const FFT_SIZE = 64; // Number of samples exposed to the UI on each frame
const SMOOTHING_FACTOR = 0.05; // Exponential smoothing; lower values make the waveform more reactive
const GAIN = 1.6; // Linear multiplier after smoothing/stretching to set overall height
const STRETCH_BASE = 0.85; // Shrinks near-zero amplitudes so quiet sections stay low
const STRETCH_SLOPE = 0.4; // Extra boost proportional to magnitude to lift peaks
const DISPLAY_MULTIPLIER = 1.4; // Final desktop-only boost before clamping

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const stretch = (value: number): number => {
  const magnitude = Math.abs(value);
  const factor = STRETCH_BASE + STRETCH_SLOPE * magnitude;
  return value * factor;
};

export class DesktopPcmProbe implements AudioLevelProbe {
  readonly fftSize: number;
  private readonly buffer: Float32Array;
  private readonly smoothingFactor: number;
  private readonly gain: number;
  private writeIndex = 0;
  private filled = 0;
  private lastValue = 0;

  constructor() {
    this.fftSize = FFT_SIZE;
    this.buffer = new Float32Array(this.fftSize);
    this.smoothingFactor = SMOOTHING_FACTOR;
    this.gain = GAIN;
  }

  pushSamples(samples: Int16Array): void {
    for (let index = 0; index < samples.length; index++) {
      const normalized = clamp(samples[index] / PCM_MAX, -1, 1);
      const smoothed =
        this.lastValue * this.smoothingFactor + normalized * (1 - this.smoothingFactor);
      const stretched = stretch(smoothed);
      const boosted = clamp(stretched * this.gain * DISPLAY_MULTIPLIER, -1, 1);

      this.buffer[this.writeIndex] = boosted;
      this.writeIndex = (this.writeIndex + 1) % this.fftSize;
      this.filled = Math.min(this.filled + 1, this.fftSize);
      this.lastValue = boosted;
    }
  }

  getFloatTimeDomainData(target: Float32Array): void {
    const available = Math.min(this.filled, this.fftSize);

    if (available === 0) {
      target.fill(0);
      return;
    }

    const length = Math.min(target.length, this.fftSize);
    const start = available === this.fftSize ? this.writeIndex : 0;

    for (let offset = 0; offset < length; offset++) {
      const index = (start + offset) % this.fftSize;
      target[offset] = this.buffer[index];
    }

    const lastValue = target[length - 1] ?? 0;
    for (let index = length; index < target.length; index++) {
      target[index] = lastValue;
    }
  }

  getByteTimeDomainData(target: Uint8Array): void {
    const floatBuffer = new Float32Array(target.length);
    this.getFloatTimeDomainData(floatBuffer);

    for (let index = 0; index < target.length; index++) {
      const clamped = clamp(floatBuffer[index], -1, 1);
      target[index] = Math.round((clamped + 1) * 127.5);
    }
  }

  dispose(): void {
    this.buffer.fill(0);
    this.writeIndex = 0;
    this.filled = 0;
    this.lastValue = 0;
  }
}
